import type { Session } from '@supabase/supabase-js';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { DEMO_ACCOUNT, isEmbedDemo } from '@/config';
import { logEvent, setAnalyticsUser } from '@/lib/analytics/firebase';
import { addFavorite } from '@/lib/data/provider';
import { getMyPushPrefs, registerForPush } from '@/lib/notifications';
import { clearPendingOnboarding, loadPendingOnboarding } from '@/lib/onboarding';
import { supabase } from '@/lib/supabase/client';

interface AuthContextValue {
  session: Session | null;
  /** True until the persisted session has been restored (or ruled out). */
  loading: boolean;
  userId: string | null;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  loading: true,
  userId: null,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  // Nothing to restore when Supabase isn't configured.
  const [loading, setLoading] = useState(() => Boolean(supabase));

  useEffect(() => {
    if (!supabase) {
      return;
    }
    let mounted = true;

    const client = supabase;

    // One-time backfill of the OAuth profile photo (Google avatar_url, Facebook
    // picture) into profiles.avatar_url, only if not already set. New signups get
    // this via the handle_new_user trigger; this covers users created earlier.
    const backfillAvatar = async (current: Session) => {
      const meta = (current.user.user_metadata ?? {}) as Record<string, unknown>;
      const photo =
        typeof meta.avatar_url === 'string'
          ? meta.avatar_url
          : typeof meta.picture === 'string'
            ? meta.picture
            : null;
      if (!photo) return;
      try {
        const { data } = await client
          .from('profiles')
          .select('avatar_url')
          .eq('id', current.user.id)
          .maybeSingle();
        if (data && !data.avatar_url) {
          await client.from('profiles').update({ avatar_url: photo }).eq('id', current.user.id);
        }
      } catch {
        // Non-fatal: the avatar simply stays as the initial-letter fallback.
      }
    };

    // Register this device for push on every authenticated session, so goal /
    // pre-match / deadline alerts arrive even when the app is closed. Previously
    // this only ran if the user opened the notifications screen, so most users
    // never got a token stored and the server had nobody to push to. Honours the
    // user's saved preferences; no-ops on web / Expo Go internally.
    // Apply the pre-auth onboarding picker's buffered follows to user_favorites
    // the moment a session exists. This used to live only on the welcome screen,
    // but sign-in routes straight to the tabs (never back to welcome), so the
    // buffer was orphaned and the user's first-run picks were silently lost.
    // Per-entry try/catch + always-clear so one stale id can't block the rest or
    // strand the buffer forever.
    const flushPendingFollows = async (uid: string) => {
      const pending = await loadPendingOnboarding().catch(() => null);
      if (!pending || pending.favorites.length === 0) return;
      for (const fav of pending.favorites) {
        try {
          await addFavorite(uid, fav.type, fav.id);
        } catch {
          // Skip a single bad entry; keep applying the others.
        }
      }
      await clearPendingOnboarding();
    };

    const ensurePushRegistered = async () => {
      try {
        const prefs = await getMyPushPrefs();
        await registerForPush({
          goalNotifications: prefs.goalNotifications,
          goalFollowedOnly: prefs.goalFollowedOnly,
          fulltimeAlerts: prefs.fulltimeAlerts,
          prematchReminders: prefs.prematchReminders,
          deadlineReminders: prefs.deadlineReminders,
          playerRatingAlerts: prefs.playerRatingAlerts,
        });
      } catch {
        // Non-fatal: notifications stay off until the user enables them manually.
      }
    };

    client.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        // Showcase frames: auto-sign-in the demo account so visitors can try
        // everything without registering. Never overrides a real session.
        if (!data.session && isEmbedDemo()) {
          client.auth
            .signInWithPassword({ email: DEMO_ACCOUNT.email, password: DEMO_ACCOUNT.password })
            .then(({ data: signIn }) => {
              if (mounted) {
                setSession(signIn.session);
                setLoading(false);
              }
            })
            .catch(() => {
              if (mounted) setLoading(false);
            });
          return;
        }
        setSession(data.session);
        setLoading(false);
        if (data.session) {
          void backfillAvatar(data.session);
          void ensurePushRegistered();
          void flushPendingFollows(data.session.user.id);
        }
      })
      .catch(() => {
        if (mounted) setLoading(false);
      });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, next) => {
      if (mounted) setSession(next);
      if (next && event === 'SIGNED_IN') {
        // High-signal analytics: tie reports to the user and mark the sign-in.
        setAnalyticsUser(next.user.id);
        logEvent('sign_in_success', { provider: next.user.app_metadata?.provider ?? 'unknown' });
        void backfillAvatar(next);
        void ensurePushRegistered();
        void flushPendingFollows(next.user.id);
      }
      if (event === 'SIGNED_OUT') {
        setAnalyticsUser(null);
      }
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, loading, userId: session?.user.id ?? null }),
    [session, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
